import React from 'react';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { OnboardingProgress } from './OnboardingProgress';

interface OnboardingStepProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  onNext?: () => void;
  onBack?: () => void;
  nextLabel?: string;
  backLabel?: string;
  showBack?: boolean;
  nextDisabled?: boolean;
  nextLoading?: boolean;
  currentStep?: number;
  totalSteps?: number;
}

export function OnboardingStep({
  title,
  subtitle,
  children,
  onNext,
  onBack,
  nextLabel = 'Volgende',
  backLabel = 'Terug',
  showBack = true,
  nextDisabled = false,
  nextLoading = false,
  currentStep,
  totalSteps,
}: OnboardingStepProps) {
  return (
    <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Header with progress and logo */}
      {currentStep && totalSteps && (
        <div className="flex items-start justify-between mb-8">
          {/* Progress indicator - left */}
          <div className="flex-shrink-0">
            <OnboardingProgress currentStep={currentStep} totalSteps={totalSteps} />
          </div>

          {/* Logo + Avatar - right */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <img
              src="https://s3-eu-west-1.amazonaws.com/contaqt-live/drag-drop-images/uploads/144/WDPA3_dibbi.png"
              alt="Avatar"
              className="w-12 h-12 rounded-full object-cover"
            />
            <img
              src="https://cdn.prod.website-files.com/68106eab8acafbe1be69f17a/6830b7212ae8f51f0b5e545b_12ebc92fd52362293d48e2636ef26ed3_logo.svg"
              alt="Dibbi Logo"
              className="h-8"
            />
          </div>
        </div>
      )}

      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">{title}</h2>
        {subtitle && (
          <p className="text-lg text-gray-600">{subtitle}</p>
        )}
      </div>

      <div className="mb-8">{children}</div>

      <div
        className="sticky bottom-0 z-10 -mx-8 md:-mx-12 -mb-8 md:-mb-12 flex items-center justify-between px-8 md:px-12 pt-6 border-t border-gray-200 bg-white rounded-b-2xl"
        style={{ paddingBottom: 'max(2rem, env(safe-area-inset-bottom))' }}
      >
        {showBack ? (
          <button
            onClick={onBack}
            className="flex items-center gap-2 px-6 py-3 text-gray-600 hover:text-gray-900 font-medium transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            {backLabel}
          </button>
        ) : (
          <div />
        )}

        {onNext && (
          <button
            onClick={onNext}
            disabled={nextDisabled || nextLoading}
            className="flex items-center gap-2 px-8 py-3 bg-[#FF6B35] text-white rounded-lg hover:bg-[#E55A2B] font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-[#FF6B35]/20"
          >
            {nextLoading ? 'Even wachten...' : nextLabel}
            <ArrowRight className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
}
